import type { CapacitySummary, DirectoryInvitation, InviteMemberInput } from "@closer/shared";
import { CAPACITY_BLOCK_MESSAGES, inviteMemberSchema } from "@closer/shared";
import { Button, FormField, Input, Select, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { tenantKey } from "../../lib/tenantQuery";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * Davet komutları (CLOS-189 `[051][S04]`).
 *
 * Oluşturma CLOS-73, yenileme/iptal CLOS-75 uçlarına gidiyor; sayfa hiçbir kuralı yeniden
 * yazmıyor.
 *
 * ### Hangi komut hangi davete sunulur
 *
 * ⚠ Karar davetin DURUMUNDAN türüyor. Kapanmış davet (kabul, ret, iptal) TARİHTİR: üzerinde
 * işlem yapılmaz. Süresi dolmuş davet ise yenilenebilir ama iptal edilemez — yenileme yeni
 * bir token üretiyor, iptal edilecek aktif bir şey ise kalmamış (uç 409 döndürürdü).
 */

type Rol = { id: string; name: string; archivedAt?: string | null };

/** Süresi dolan davet yeniden gönderilebilir; kapanmış olan gönderilemez. */
function yenilenebilir(status: DirectoryInvitation["status"]): boolean {
  return status === "pending" || status === "expired";
}

export function InvitationRowActions({
  tenantId,
  invitation,
}: {
  tenantId: string;
  invitation: DirectoryInvitation;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rolAcik, setRolAcik] = useState(false);
  const [roleId, setRoleId] = useState(invitation.roleId ?? "");

  const komut = useMutation({
    mutationFn: async (yol: "renew" | "cancel") => {
      await api(`/api/tenants/${tenantId}/invites/${invitation.id}/${yol}`, {
        method: "POST",
        tenantId,
      });
      return yol;
    },
    onSuccess: async (yol) => {
      /**
       * İptal koltuk BIRAKIYOR (`[036]`), bu yüzden kapasite de tazeleniyor: özet eski
       * sayıda kalsaydı yönetici geri çektiği bir davet yüzünden yeni birini davet
       * edemediğini sanırdı.
       */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "invites") }),
        queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "capacity") }),
      ]);
      toast.success(yol === "renew" ? "Davet yeniden gönderildi" : "Davet iptal edildi");
    },
    /** Sunucunun cümlesi: "bu davet artık yenilenemez" gibi bir ret kendi nedenini taşır. */
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      ),
  });

  /**
   * BEKLEYEN DAVETİN ROLÜNÜ DEĞİŞTİRME (CLOS-1109 · `PATCH .../invites/:inviteId/role`).
   *
   * ### ⚠ Uç yazılmıştı, çağıran yoktu
   *
   * Sunucu bu komutu CLOS-89 `[039]` ile aldı ve gerekçesi de yazılı ("verme sınırı burada
   * da uygulanıyor — aksi hâlde davet, rol atamanın etrafından dolaşan bir yol olurdu").
   * Ekran ise yalnız `renew` ve `cancel` gönderiyordu: yanlış rolle gönderilmiş bir daveti
   * DÜZELTMENİN tek yolu iptal edip yeniden göndermekti.
   *
   * Bu, denetimin "oluşturma bağlı, düzeltme bağlı değil" desenlerinden biri (CLOS-1109).
   */
  const rolSorgusu = useQuery({
    queryKey: tenantKey(tenantId, "roles"),
    enabled: rolAcik,
    queryFn: async () => (await api<Rol[]>("/api/roles", { tenantId })).data,
  });

  const rolDegistir = useMutation({
    mutationFn: async () =>
      api(`/api/tenants/${tenantId}/invites/${invitation.id}/role`, {
        method: "PATCH",
        body: JSON.stringify({ roleId }),
        tenantId,
      }),
    onSuccess: async () => {
      /**
       * ⚠ Kapasite tazelenmiyor: rol değişimi koltuk sayısını DEĞİŞTİRMİYOR. Yenileme ve
       * iptalde tazeliyoruz çünkü onlar rezervasyona dokunuyor; burada gereksiz bir istek
       * olurdu.
       */
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "invites") });
      setRolAcik(false);
      toast.success("Davetin rolü güncellendi");
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      ),
  });

  /**
   * ⚠ Arşivlenmiş rol SEÇENEK OLARAK sunulmuyor (`MemberActions` ile aynı kural): sunucu
   * onu reddeder ve kullanıcıya reddedilecek bir seçenek göstermek, hatayı forma taşımak
   * olurdu.
   */
  const roller = (rolSorgusu.data ?? []).filter((r) => !r.archivedAt);

  return (
    <div className="flex flex-wrap items-end gap-1">
      {yenilenebilir(invitation.status) ? (
        <Button
          variant="ghost"
          size="sm"
          loading={komut.isPending}
          onClick={() => komut.mutate("renew")}
        >
          Yeniden gönder
        </Button>
      ) : null}
      {/*
        ⚠ YALNIZ BEKLEYEN DAVETTE: süresi dolmuş ya da kapanmış davetin rolünü değiştirmek
        anlamsız — kabul edilecek bir şey kalmamış. Kural `cancel` ile aynı ve bilerek.
      */}
      {invitation.status === "pending" && !rolAcik ? (
        <Button variant="ghost" size="sm" onClick={() => setRolAcik(true)}>
          Rolü değiştir
        </Button>
      ) : null}
      {invitation.status === "pending" && rolAcik ? (
        <>
          <FormField label="Yeni rol">
            <Select
              aria-label="Yeni rol"
              value={roleId}
              onChange={(olay) => setRoleId(olay.target.value)}
            >
              <option value="">Seçiniz</option>
              {roller.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </FormField>
          <Button
            size="sm"
            loading={rolDegistir.isPending}
            disabled={roleId === ""}
            onClick={() => rolDegistir.mutate()}
          >
            Kaydet
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setRolAcik(false)}>
            Vazgeç
          </Button>
        </>
      ) : null}
      {invitation.status === "pending" ? (
        <Button
          variant="ghost"
          size="sm"
          loading={komut.isPending}
          onClick={() => komut.mutate("cancel")}
        >
          İptal et
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Yeni davet formu.
 *
 * ⚠ Kapasite yoksa gönderim KAPALI ve nedeni yazıyor. Açık bıraksaydık kullanıcı formu
 * doldurur, gönderir ve ancak o zaman reddedilirdi; üstelik iki engel (koltuk dolu /
 * lisans hak vermiyor) farklı adımlar gerektiriyor.
 */
export function InviteForm({
  tenantId,
  capacity,
}: {
  tenantId: string;
  capacity: CapacitySummary | undefined;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const form = useZodForm(inviteMemberSchema);

  const rolSorgusu = useQuery({
    queryKey: tenantKey(tenantId, "roles"),
    queryFn: async () => (await api<Rol[]>("/api/roles", { tenantId })).data,
  });

  /** Kapasite bilinmiyorsa engellemiyoruz: kararı sunucu zaten veriyor (fail-closed orada). */
  const engelli = capacity?.canInvite === false;

  const gonder = form.handleSubmit(async (girdi: InviteMemberInput) => {
    try {
      await api(`/api/tenants/${tenantId}/invites`, {
        method: "POST",
        body: JSON.stringify(girdi),
        tenantId,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "invites") }),
        queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "capacity") }),
      ]);
      form.reset();
      toast.success("Davet gönderildi");
    } catch (error) {
      /**
       * ⚠ Alan hatası FORMA işleniyor: "bu kullanıcı zaten üye" cevabı bir toast olarak
       * geçip gitseydi kullanıcı hangi alanı düzelteceğini bilemezdi.
       */

      if (error instanceof ApiRequestError && error.fields) {
        applyServerErrors(form, error);
        return;
      }
      toast.error(
        error instanceof ApiRequestError ? error.message : "Davet gönderilemedi, tekrar deneyin.",
      );
    }
  });

  return (
    <form noValidate className="flex flex-col gap-3" onSubmit={gonder}>
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
        <FormField label="E-posta" error={form.formState.errors.email?.message} required>
          <Input placeholder="ornek@firma.com" {...form.register("email")} />
        </FormField>
        <FormField label="Rol" error={form.formState.errors.roleId?.message} required>
          <Select {...form.register("roleId")}>
            <option value="">Seçiniz</option>
            {(rolSorgusu.data ?? [])
              .filter((rol) => !rol.archivedAt)
              .map((rol) => (
                <option key={rol.id} value={rol.id}>
                  {rol.name}
                </option>
              ))}
          </Select>
        </FormField>
      </div>
      {engelli && capacity?.blockedReason ? (
        <p className="text-sm text-warning">{CAPACITY_BLOCK_MESSAGES[capacity.blockedReason]}</p>
      ) : null}
      <div className="flex justify-end">
        <Button type="submit" disabled={engelli} loading={form.formState.isSubmitting}>
          Davet gönder
        </Button>
      </div>
    </form>
  );
}
